import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { readHomeView } from "@/components/personal-apps/homeView";

type Panel = "discover" | "library" | null;
export const DISCOVERY_PANEL_STORAGE_KEY = "ato.discovery.panel.v1";

function readPanel(): Panel {
  const params = new URLSearchParams(window.location.search);
  const view = window.location.search ? params.get("view") : readHomeView();
  // Explicit application/detail links open their target, never a saved sheet.
  if (window.location.pathname !== "/" || view === "my-apps" || params.has("app") || params.has("c") || params.has("detail")) return null;
  try {
    const panel = localStorage.getItem(DISCOVERY_PANEL_STORAGE_KEY);
    if (panel === "library" && window.innerWidth < 1280) return panel;
    if (panel === "discover" && window.innerWidth < 900) return panel;
  } catch {
    // The navigation controls remain usable without storage.
  }
  return null;
}
type Panels = {
  panel: Panel;
  setPanel: (panel: Panel) => void;
  workspaceActive: boolean;
  setWorkspaceActive: (active: boolean) => void;
  workspaceView: "list" | "play" | null;
  setWorkspaceView: (view: "list" | "play" | null) => void;
  libraryActive: boolean;
  setLibraryActive: (active: boolean) => void;
  /** Header Discover: the mounted workspace returns to its Grid. */
  showGrid: () => boolean;
  setShowGrid: (show: (() => void) | null) => void;
};
const Context = createContext<Panels | null>(null);

/** Header and workspace share one panel state; opening a sheet never navigates. */
export function DiscoveryPanelsProvider({ children }: { children: ReactNode }) {
  const [panel, setPanel] = useState<Panel>(readPanel);
  const [workspaceActive, setWorkspaceActive] = useState(false);
  const [workspaceView, setWorkspaceView] = useState<"list" | "play" | null>(null);
  const [libraryActive, setLibraryActive] = useState(false);
  const grid = useRef<(() => void) | null>(null);
  const showGrid = useCallback(() => {
    if (!grid.current) return false;
    grid.current();
    return true;
  }, []);
  const setShowGrid = useCallback((show: (() => void) | null) => { grid.current = show; }, []);
  useEffect(() => {
    // Workspace cleanup clears the transient sheet; keep its last saved choice.
    if (!workspaceActive) return;
    try {
      if (panel) localStorage.setItem(DISCOVERY_PANEL_STORAGE_KEY, panel);
      else localStorage.removeItem(DISCOVERY_PANEL_STORAGE_KEY);
    } catch {
      // Device-local persistence is best effort.
    }
  }, [panel, workspaceActive]);
  const value = useMemo(
    () => ({
      panel,
      setPanel,
      workspaceActive,
      setWorkspaceActive,
      workspaceView,
      setWorkspaceView,
      libraryActive,
      setLibraryActive,
      showGrid,
      setShowGrid,
    }),
    [panel, workspaceActive, workspaceView, libraryActive, showGrid, setShowGrid],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useDiscoveryPanels() {
  return useContext(Context);
}
